<div class="main-content">
	<div class="container-fluid">
		<div class="row">
			<div class="col-md-12">
				<div class="card">
					<div class="card-header card-header-{{ color }}">
						<h4 class="card-title">
							<i class="material-icons clickable-icon" (click)="back()">arrow_back</i>
							Simple Trial Balance
						</h4>
					</div>
					<div class="card-body">

						<form [formGroup]="formGroup" (ngSubmit)="loadTrialBalance()">
							<div class="row align-items-center g-2 mb-3">
								<div class="col-md-3">
									<mat-form-field appearance="outline" class="w-100">
										<mat-label>Period</mat-label>
										<mat-select formControlName="period" (selectionChange)="onPeriodChange()">
											<mat-option *ngFor="let p of periods" [value]="p.periodno">
												{{ p.periodno }} &mdash; {{ p.lastdate_in_period }}
											</mat-option>
										</mat-select>
									</mat-form-field>
								</div>
								<div class="col-md-3">
									<mat-form-field appearance="outline" class="w-100">
										<mat-label>Posted</mat-label>
										<mat-select formControlName="posted">
											<mat-option *ngFor="let option of postedOptions" [value]="option.value">
												{{ option.label }}
											</mat-option>
										</mat-select>
									</mat-form-field>
								</div>
								<div class="col-auto">
									<button mat-raised-button color="primary" type="submit" [disabled]="!formGroup.get('period')?.value || loading">
										<mat-icon>play_arrow</mat-icon> Run
									</button>
								</div>
								<div class="col-auto" *ngIf="hasRun && trialBalanceList.length">
									<mat-slide-toggle [checked]="hideZero" (change)="toggleHideZero()" color="primary">
										Hide zero balances
									</mat-slide-toggle>
								</div>
								<div class="col-auto">
									<button mat-raised-button class="btn-success" type="button" (click)="exportExcel()" [disabled]="!trialBalanceList.length || exporting">
										<i class="fa fa-file-excel-o"></i> Export XLS
									</button>
									<button mat-raised-button class="btn-danger" type="button" (click)="exportPdf()" [disabled]="!trialBalanceList.length || exporting">
										<i class="fa fa-file-pdf-o"></i> Export PDF
									</button>
								</div>
							</div>
						</form>

						<div *ngIf="loading" class="text-center py-5">
							<svg class="spinner" width="65px" height="65px" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
								<circle class="path" fill="none" stroke-width="6" stroke-linecap="round" cx="33" cy="33" r="30"></circle>
							</svg>
						</div>

						<ng-container *ngIf="!loading && hasRun">

							<div *ngIf="!trialBalanceList.length" class="text-center py-4 text-muted">
								No data found for the selected period.
							</div>
							<div *ngIf="allRowsHiddenByZeroFilter()" class="text-center py-4 text-muted">
								All rows are currently hidden by the zero-balance filter.
								<button mat-button color="primary" type="button" (click)="toggleHideZero()">Show zero balances</button>
							</div>

							<ng-container *ngIf="trialBalanceList.length">
								<div class="tb-header mb-2 d-flex flex-wrap align-items-center justify-content-between gap-2">
									<span class="tb-period-label">Period ending: <strong>{{ selectedPeriodLabel }}</strong></span>
									<span class="tb-balance-hint" [class.tb-balanced]="tbBalanced()" [class.tb-unbalanced]="!tbBalanced()">
										<span *ngIf="tbBalanced()">Total debits and credits match</span>
										<span *ngIf="!tbBalanced()">Out of balance by {{ (debitGrandTotal - creditGrandTotal) | number:'1.2-2' }}</span>
									</span>
								</div>

								<div class="table-responsive tb-table-wrap">
									<table class="table tb-table">
										<thead>
											<tr class="tb-col-header">
												<th class="col-code sortable" (click)="sort('accountcode')">
													Account
													<mat-icon class="sort-icon">{{ sortCol === 'accountcode' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}</mat-icon>
												</th>
												<th class="col-name sortable" (click)="sort('accountname')">
													Name
													<mat-icon class="sort-icon">{{ sortCol === 'accountname' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}</mat-icon>
												</th>
												<th class="col-debit sortable" (click)="sort('balance_at_date')">
													Debit
													<mat-icon class="sort-icon">{{ sortCol === 'balance_at_date' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}</mat-icon>
												</th>
												<th class="col-credit sortable" (click)="sort('balance_at_date')">
													Credit
													<mat-icon class="sort-icon">{{ sortCol === 'balance_at_date' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}</mat-icon>
												</th>
											</tr>
										</thead>
										<tbody>
											<ng-container *ngFor="let grp of groupedRows">
												<tr class="tb-group-row">
													<td colspan="4">{{ grp.group }}</td>
												</tr>
												<ng-container *ngFor="let row of grp.rows">
													<ng-container *ngIf="visibleTagBreakdown(row).length === 0; else taggedRow">
														<tr
															class="tb-data-row tb-account-link"
															(click)="openAccount(row)"
															title="View account inquiry">
															<td class="col-code">{{ row.accountcode }}</td>
															<td class="col-name">{{ row.accountname }}</td>
															<td class="col-debit">{{ formatMoney(debitAmount(row.balance_at_date)) }}</td>
															<td class="col-credit">{{ formatMoney(creditAmount(row.balance_at_date)) }}</td>
														</tr>
													</ng-container>
													<ng-template #taggedRow>
														<tr *ngFor="let tb of visibleTagBreakdown(row); let i = index"
															class="tb-data-row tb-tag-row tb-account-link"
															[class.tb-tag-row-first]="i === 0"
															[class.tb-tag-row-last]="i === visibleTagBreakdown(row).length - 1"
															(click)="openAccount(row)"
															title="View account inquiry">
															<td class="col-code">{{ row.accountcode }}</td>
															<td class="col-name">
																<div class="tb-name-with-tag">
																	<span class="tb-name-text">{{ row.accountname }}</span>
																	<span class="tb-tag-pill"
																		[class.tb-tag-untagged]="tb.tagdescription === 'Untagged'"
																		[class.tb-tag-other]="tb.tagdescription === 'Other'">{{ tb.tagdescription }}</span>
																</div>
															</td>
															<td class="col-debit">{{ formatMoney(debitAmount(tb.balance_at_date)) }}</td>
															<td class="col-credit">{{ formatMoney(creditAmount(tb.balance_at_date)) }}</td>
														</tr>
														<tr
															class="tb-tag-total-row tb-account-link"
															(click)="openAccount(row)"
															title="View account inquiry">
															<td class="col-code">{{ row.accountcode }}</td>
															<td class="col-name col-subtotal-label tb-tag-total-label">{{ row.accountname }} Total</td>
															<td class="col-debit">{{ formatMoney(debitAmount(row.balance_at_date)) }}</td>
															<td class="col-credit">{{ formatMoney(creditAmount(row.balance_at_date)) }}</td>
														</tr>
													</ng-template>
												</ng-container>
												<tr class="tb-subtotal-row">
													<td class="col-code"></td>
													<td class="col-name col-subtotal-label">{{ grp.group }} Total</td>
													<td class="col-debit">{{ formatMoney(grp.subtotalDebit > 0 ? grp.subtotalDebit : null) }}</td>
													<td class="col-credit">{{ formatMoney(grp.subtotalCredit > 0 ? grp.subtotalCredit : null) }}</td>
												</tr>
											</ng-container>
										</tbody>
										<tfoot>
											<tr class="tb-grand-total-row">
												<td class="col-code"></td>
												<td class="col-name col-subtotal-label">Grand Total</td>
												<td class="col-debit">{{ formatMoney(debitGrandTotal) }}</td>
												<td class="col-credit">{{ formatMoney(creditGrandTotal) }}</td>
											</tr>
										</tfoot>
									</table>
								</div>
							</ng-container>

						</ng-container>

					</div>
				</div>
			</div>
		</div>
	</div>
</div>
